import { z } from "zod";

/** Documents attached to a request (WP-2.2, ADR-0020). Shared by the API and the web app. */

export const DOCUMENT_MAX_BYTES = 15 * 1024 * 1024;
export const DOCUMENT_MAX_PER_REQUEST = 20;
/** For the file picker; the server decides the type from the bytes. */
export const DOCUMENT_ACCEPT = ".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp";

export const DOCUMENT_STATUSES = ["PENDING", "PROCESSING", "READY", "FAILED"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/** Why an upload was refused (400 `details.reason`) or reading failed (`failureReason`). */
export const DOCUMENT_PROBLEMS = [
  "unsupported_type",
  "too_large",
  "too_many",
  "empty",
  "unreadable",
  "encrypted",
  "scan_too_long",
  "not_a_document",
  "processing_failed",
] as const;
export type DocumentProblem = (typeof DOCUMENT_PROBLEMS)[number];

export const DOCUMENT_KINDS = [
  "quote",
  "proposal",
  "statement_of_work",
  "contract",
  "invoice",
  "cv",
  "requirements",
  "other",
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/**
 * What task `doc_extraction` returns (prompt `procuremate.doc_extraction`). Every item carries a
 * verbatim quote; code drops items whose quote is not in the document. Strict structured output:
 * every property is required (absent = null or []).
 */
export const documentFactsSchema = z.object({
  kind: z.enum(DOCUMENT_KINDS),
  title: z.string().nullable(),
  supplier: z.string().nullable().describe("The issuing company as written, or null."),
  documentDate: z.string().nullable().describe("As written in the document, or null."),
  validUntil: z.string().nullable().describe("Offer validity or expiry as written, or null."),
  summary: z.string().describe("At most three sentences, in the language requested."),
  amounts: z.array(z.object({ label: z.string(), amount: z.string(), quote: z.string() })),
  fields: z.array(
    z.object({
      fieldKey: z.string().describe("A key from the field list."),
      value: z.string().describe("The value as the document states it (lists: items separated by '; ')."),
      quote: z.string().describe("Verbatim text from the document supporting the value."),
    }),
  ),
  terms: z.array(z.object({ text: z.string(), quote: z.string() })).describe("Notable commercial terms."),
});
export type DocumentFacts = z.infer<typeof documentFactsSchema>;

export const documentViewSchema = z.object({
  id: z.uuid(),
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int(),
  status: z.enum(DOCUMENT_STATUSES),
  failureReason: z.enum(DOCUMENT_PROBLEMS).nullable(),
  /** TEXT | PDF | DOCX | OCR once read. */
  method: z.string().nullable(),
  kind: z.enum(DOCUMENT_KINDS).nullable(),
  supplier: z.string().nullable(),
  summary: z.string().nullable(),
  createdAt: z.string(),
});
export type DocumentView = z.infer<typeof documentViewSchema>;
